/**
 * Maps a free-text province/state onto the short code the signup forms use.
 *
 * Incoming address data is not normalised. The same province arrives as "ON",
 * "Ontario", "On"; Quebec arrives accented as "Québec"; British Columbia as
 * "B.C." or "B-C"; and occasionally the city leaks into the state field
 * ("Saskatoon" for Saskatchewan).
 *
 * Codes here match `short_name` in the states_and_provinces table, which is what
 * the province dropdowns are built from. Note PEI: the table and the dropdown
 * both use "PEI", while incoming data usually says "PE" — that mismatch is the
 * main reason this module exists.
 *
 * Both countries are covered. Canadian signups only use the CA side today; the
 * US table is here so the same helper works if a US form is ever added.
 */

import { convertAccentNames } from '../commonfunctions/commonFunction';

export type RegionCountry = 'CA' | 'US';

interface RegionEntry {
  /** Short code as stored in states_and_provinces.short_name. */
  code: string;
  /** Official name. */
  name: string;
  /** Postal/ZIP prefixes, as in the table's `zipcodes` column. CA only. */
  prefixes?: string[];
  /** Other spellings seen in incoming data: abbreviations, French names, old codes. */
  aliases?: string[];
  /**
   * Cities that sometimes arrive in the state field by mistake. Only cities
   * belonging to exactly one province are listed — "London", "Windsor" and
   * "Stratford" exist in more than one and are deliberately absent.
   */
  cities?: string[];
}

const CA_REGIONS: RegionEntry[] = [
  {
    code: 'AB', name: 'Alberta', prefixes: ['T'],
    aliases: ['ALTA'],
    cities: ['CALGARY', 'EDMONTON', 'RED DEER', 'LETHBRIDGE'],
  },
  {
    code: 'BC', name: 'British Columbia', prefixes: ['V'],
    aliases: ['BRITISH COLUMBIA', 'COLOMBIE BRITANNIQUE', 'COLOMBIE-BRITANNIQUE'],
    cities: ['VANCOUVER', 'VICTORIA', 'SURREY', 'BURNABY', 'KELOWNA', 'ABBOTSFORD'],
  },
  {
    code: 'MB', name: 'Manitoba', prefixes: ['R'],
    aliases: ['MAN'],
    cities: ['WINNIPEG', 'BRANDON'],
  },
  {
    code: 'NB', name: 'New Brunswick', prefixes: ['E'],
    aliases: ['NEW BRUNSWICK', 'NOUVEAU BRUNSWICK', 'NOUVEAU-BRUNSWICK'],
    cities: ['MONCTON', 'FREDERICTON', 'SAINT JOHN'],
  },
  {
    code: 'NL', name: 'Newfoundland and Labrador', prefixes: ['A'],
    aliases: [
      'NF', 'LB', 'NFLD', 'NEWFOUNDLAND', 'LABRADOR',
      'NEWFOUNDLAND AND LABRADOR', 'NEWFOUNDLAND & LABRADOR',
      'TERRE NEUVE', 'TERRE-NEUVE', 'TERRE NEUVE ET LABRADOR',
    ],
    cities: ['ST JOHNS', "ST. JOHN'S", 'MOUNT PEARL', 'CORNER BROOK'],
  },
  {
    code: 'NS', name: 'Nova Scotia', prefixes: ['B'],
    aliases: ['NOVA SCOTIA', 'NOUVELLE ECOSSE', 'NOUVELLE-ECOSSE'],
    cities: ['HALIFAX', 'DARTMOUTH', 'SYDNEY'],
  },
  {
    code: 'NT', name: 'Northwest Territories', prefixes: ['X'],
    aliases: [
      'NWT', 'NORTHWEST TERRITORIES', 'NORTH WEST TERRITORIES',
      'TERRITOIRES DU NORD OUEST', 'TERRITOIRES DU NORD-OUEST',
    ],
    cities: ['YELLOWKNIFE'],
  },
  {
    code: 'NU', name: 'Nunavut', prefixes: ['X'],
    cities: ['IQALUIT'],
  },
  {
    code: 'ON', name: 'Ontario', prefixes: ['K', 'L', 'M', 'N', 'P'],
    aliases: ['ONT'],
    cities: [
      'TORONTO', 'OTTAWA', 'MISSISSAUGA', 'BRAMPTON', 'HAMILTON', 'KITCHENER',
      'OSHAWA', 'BARRIE', 'GUELPH', 'KINGSTON', 'THUNDER BAY', 'SUDBURY',
      'SCARBOROUGH', 'ETOBICOKE', 'NORTH YORK', 'MARKHAM', 'VAUGHAN', 'BURLINGTON',
    ],
  },
  {
    // The table and the dropdown use PEI; incoming data usually says PE.
    code: 'PEI', name: 'Prince Edward Island', prefixes: ['C'],
    aliases: [
      'PE', 'PRINCE EDWARD ISLAND', 'ILE DU PRINCE EDOUARD', 'ILE-DU-PRINCE-EDOUARD',
    ],
    cities: ['CHARLOTTETOWN', 'SUMMERSIDE'],
  },
  {
    code: 'QC', name: 'Quebec', prefixes: ['G', 'H', 'J'],
    // "Québec" loses its accent before lookup, so the plain spelling covers it.
    aliases: ['QUE', 'PQ', 'QUEBEC CITY', 'VILLE DE QUEBEC'],
    cities: ['MONTREAL', 'LAVAL', 'GATINEAU', 'SHERBROOKE', 'LONGUEUIL', 'TROIS RIVIERES'],
  },
  {
    code: 'SK', name: 'Saskatchewan', prefixes: ['S'],
    aliases: ['SASK'],
    cities: ['SASKATOON', 'REGINA', 'PRINCE ALBERT', 'MOOSE JAW'],
  },
  {
    code: 'YT', name: 'Yukon', prefixes: ['Y'],
    aliases: ['YK', 'YUKON TERRITORY', 'YUKON TERRITORIES'],
    cities: ['WHITEHORSE'],
  },
];

const US_REGIONS: RegionEntry[] = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'DC', name: 'District of Columbia', aliases: ['WASHINGTON DC', 'WASHINGTON D C'] },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'PR', name: 'Puerto Rico' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
  // Military/diplomatic codes carried in the same table.
  { code: 'AA', name: 'AA' },
  { code: 'AE', name: 'AE' },
  { code: 'AP', name: 'AP' },
];

const REGIONS: Record<RegionCountry, RegionEntry[]> = {
  CA: CA_REGIONS,
  US: US_REGIONS,
};

/**
 * Collapses a value to a comparison key: accents stripped, uppercased, and
 * everything that is not a letter or digit removed. That folds "B.C.", "B-C"
 * and "b c" onto "BC", and "Québec" onto "QUEBEC".
 */
function toKey(value: any): string {
  if (value === null || value === undefined) return '';
  const text = String(value);
  if (!text.trim()) return '';
  return convertAccentNames(text).toUpperCase().replace(/[^A-Z0-9]/g, '');
}

/** code/name/alias key → code, built once per country. */
const EXACT_LOOKUP: Record<RegionCountry, Record<string, string>> = {
  CA: {},
  US: {},
};
/** city key → code, kept separate so it is only consulted as a fallback. */
const CITY_LOOKUP: Record<RegionCountry, Record<string, string>> = {
  CA: {},
  US: {},
};

(Object.keys(REGIONS) as RegionCountry[]).forEach((country) => {
  for (const entry of REGIONS[country]) {
    for (const candidate of [entry.code, entry.name, ...(entry.aliases ?? [])]) {
      const key = toKey(candidate);
      if (key) EXACT_LOOKUP[country][key] = entry.code;
    }
    for (const city of entry.cities ?? []) {
      const key = toKey(city);
      // First province to claim a city wins; ambiguous cities are not listed.
      if (key && !CITY_LOOKUP[country][key]) CITY_LOOKUP[country][key] = entry.code;
    }
  }
});

/**
 * Province/state text → short code, or '' when it cannot be resolved.
 * Tries codes, names and known aliases first, then the city fallback.
 */
export function normalizeRegionCode(value: any, country: RegionCountry = 'CA'): string {
  const key = toKey(value);
  if (!key) return '';
  return EXACT_LOOKUP[country][key] ?? CITY_LOOKUP[country][key] ?? '';
}

/** Canadian province text → short code ('ON', 'PEI', 'QC', …), or ''. */
export function normalizeCanadianProvince(value: any): string {
  return normalizeRegionCode(value, 'CA');
}

/** US state text → short code ('NY', 'CA', …), or ''. */
export function normalizeUsState(value: any): string {
  return normalizeRegionCode(value, 'US');
}

/**
 * Last-resort province lookup from a Canadian postal code's first letter, for
 * when the state field is missing or unrecognisable. Returns '' for X, which
 * covers both Northwest Territories and Nunavut and so cannot be decided here.
 */
export function canadianProvinceFromPostalCode(postalCode: any): string {
  const key = toKey(postalCode);
  if (!key) return '';

  const prefix = key.charAt(0);
  const matches = CA_REGIONS.filter((entry) => entry.prefixes?.includes(prefix));
  return matches.length === 1 ? matches[0].code : '';
}

/** Official name for a short code, e.g. 'PEI' → 'Prince Edward Island'. */
export function regionName(code: any, country: RegionCountry = 'CA'): string {
  const key = toKey(code);
  const entry = REGIONS[country].find((item) => toKey(item.code) === key);
  return entry?.name ?? '';
}
